import { ReactElement } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Icon } from '@mdi/react';

type Props = {
  iconPath?: string;
  customIcon?: JSX.Element;
  iconSize?: number;
  subtitle?: string | ReactElement | ReactElement[];
  mainText?: string | ReactElement | ReactElement[];
};

/**
 * @summary Заглушка для таблицы.
 *
 * @param iconPath - Path нужной иконки(используется библиотека mdi/react и mdi/js).
 * @param customIcon - Элемент, замещающий собой иконку. Полезно использовать, когда 
 * в mdi/js нет подходящей иконки. Если передаётся, то iconPath будет игнорироваться,
 * и будет отображаться переданный элемент.
 * @param iconSize - Размер иконки. По умолчанию 200 пикселей.
 * @param subtitle - Заголовок.
 * @param mainText - Основное сообщение заглушки.
 *
 * @description Содержимое этого компонента должно отображаться внутри(или вместо)
 * таблицы, когда данные отсутствуют. Этот компонент предназначен для того, чтобы
 * дать понять пользователю, почему данных в таблице нет и как их добавить.
 * 
 * @example
 * <TablePlaceholder
      customIcon={<img src={icon} />}
      subtitle="No quota created"
      mainText={tr(
        'To create quotas, click the <a href="%s">add button</a>',
        `${modulePath}/create`
      )}
    />
 */
export const TablePlaceholder = ({
  iconPath,
  customIcon,
  iconSize = 200,
  subtitle,
  mainText,
}: Props) => {
  const theme = useTheme();
  let resultIcon: JSX.Element | null = null;
  if (customIcon) {
    resultIcon = customIcon;
  } else if (iconPath) {
    resultIcon = <Icon path={iconPath} size={`${iconSize}px`} color={theme.palette.grey.A100} />;
  }
  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      mt={15}
      textAlign="center"
      sx={{
        position: 'relative',
        zIndex: 1,
      }}
    >
      {resultIcon}
      {subtitle && (
        <Typography variant="subtitle2" gutterBottom>
          {subtitle}
        </Typography>
      )}
      {mainText && <Typography variant="body2">{mainText}</Typography>}
    </Box>
  );
};
